import { IFilterObj } from '@/src/components/filters';
import { Grid, Paper, Switch, Skeleton } from '@mui/material';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import _ from 'lodash';
import { FiltersWrapper } from '../../../common/features';
import { getScenarioDetails } from '../../../home/store/simulator-home-async-thunks';
import {
  getPPGLevelImpactData,
  getSummaryData
} from '../../store/simulation-summary-slice';
import {
  handleToggleValueChange,
  handleToggleViewChange
} from '../../store/summary-slice';
import { titleData } from '../../utils/constants';
import ImpactAtPPGLevelTable from '../impact-at-ppg-level/impact-at-ppg-level-table';
import BarChartContainer from '../summary-bar-chart/bar-chart-container';
import MetricsCardComponent from '../summary-bar-chart/metrics-card-component';

import { setTableMetaData } from '../../../common/features/table/store/table-slice';


interface ISummaryProps {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
  /**
   * If set to true, Filters will not be visible and data will be fetched with all filters
   */
  fetchDataWithEmptyFilters: boolean;
  mode?: 'summary' | 'comparison';
}

export default function Summary({
  sourceId,
  moduleId,
  scenarioId,
  fetchDataWithEmptyFilters = false,
  mode = 'summary'
}: ISummaryProps) {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent } = useAppInsights();

  const { isUnitSelected, isPlotSelected } = useAppSelector(
    (state) => state.summary
  );
  const { summary: summaryAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const currentScenario = useAppSelector(
    (state) => state.simulatorHome.currentScenario
  );
  const dtStatusLabel = currentScenario?.is_dt_enabled === undefined ? '--' : currentScenario.is_dt_enabled ? 'Enabled' : 'Disabled';
  const isTargetEnabled = useAppSelector((state) => state.scenarioComparison.isTargetEnabled);
  const currencyFlag = mode === 'comparison' ? isTargetEnabled : undefined;
  const { ppgLevelImpactData } = useAppSelector((state) => state.summary);
  //Taking summary data from summary state
  const summaryDataState = useAppSelector((state) => state.summary);
  const {
    summaryMetrics: summaryData,
    isFetchingSummaryMetadata: fetchingSummaryData
  } = summaryDataState;

  useEffect(() => {
    appDispatch(setTableMetaData({ tableData: ppgLevelImpactData }));
  }, [ppgLevelImpactData]);

  // adding telemetry log for component mount with context
  useEffect(() => {
    trackEvent('Summary_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      mode,
      fetchDataWithEmptyFilters,
      isTargetEnabled: isTargetEnabled ?? false
    });
  }, []);

  const toggleSwitch = (e) => {
    // adding telemetry log for unit toggle change
    trackEvent('Summary_UnitToggleChanged', {
      previousValue: isUnitSelected,
      newValue: e.target.checked,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      mode
    });
    appDispatch(handleToggleValueChange({ isUnitSelected: e.target.checked }));
  };

  // toggle handler to switch between plot and text/table
  const togglePlotSwitch = (e) => {
    // adding telemetry log for plot/table view toggle change
    trackEvent('Summary_ViewToggleChanged', {
      previousValue: isPlotSelected,
      newValue: e.target.checked,
      viewMode: e.target.checked ? 'table' : 'plot',
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      mode
    });
    appDispatch(handleToggleViewChange({ isPlotSelected: e.target.checked }));
  };

  /**
   * Method to get data for bar charts, and PPG level impact table data
   * This method needs to be called manually if filters are not be shown
   * If filters are present, the method gets called automatically
   */
  const handleUpdateData = (filters: IFilterObj[]) => {
    // adding telemetry log for filter apply action
    trackEvent('Summary_FiltersApplied', {
      filterCount: filters?.length || 0,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      mode,
      isTargetEnabled: isTargetEnabled ?? false
    });

    const filterInputs = {
      filter_inputs: filters,
    };
    // get summary card details
    appDispatch(
      getScenarioDetails({
        scenarioId,
        sourceId
      })
    );
    // summary api call on load
    appDispatch(
      getSummaryData({
        source_id: sourceId,
        module_id: moduleId,
        scenario_id: scenarioId,
        filter_inputs: filterInputs,
        ...(mode === 'comparison' && isTargetEnabled !== undefined
          && { currency_toggle: isTargetEnabled })
      })
    );
    // get ppg level impact data on load
    appDispatch(
      getPPGLevelImpactData({
        dataInput: filterInputs,
        sourceId,
        moduleId,
        scenarioId,
        ...(mode === 'comparison' && isTargetEnabled !== undefined
          && { currency_toggle: isTargetEnabled })
      })
    );
  };

  useEffect(() => {
    if (fetchDataWithEmptyFilters) {
      handleUpdateData([]);
    }

    // setting the default value as plot
    appDispatch(handleToggleViewChange({ isPlotSelected: false }));
  }, [currencyFlag]);

  return (
    <Grid container>
      {!fetchDataWithEmptyFilters ? (
        <FiltersWrapper
          accessConfig={summaryAccessConfig}
          externalReset={false}
          moduleId={moduleId}
          onValueApply={handleUpdateData}
          onFilterFieldsFetched={handleUpdateData}
          scenarioId={scenarioId}
          setIsFilterValueChanged={() => 0}
          sourceId={sourceId}
        />
      ) : null}
      <Grid item xs={12}>
        <MetricsCardComponent />
      </Grid>
      <Grid item xs={12}>
        <div style={{ maxHeight: '87vh', overflow: 'auto' }}>
          <div className='chart-container '>
            <Paper
              className='summary-div'
              elevation={0}
              data-testid='bar-chart-container'
            >
              <div
                className='container-title title-margin'
                style={{
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <div>{titleData.summaryTitle}</div>

                {fetchingSummaryData ? (
                  <Skeleton
                    variant='rectangular'
                    sx={{ width: 200, height: 20 }}
                  />
                ) : (
                  <div className='toggle-div'>
                    <div style={{ marginRight: '6px' }}>
                      <span
                        style={{
                          marginRight: '4px',
                          color: '#0000a0',
                          fontSize: '12px',
                          fontWeight: '700'
                        }}
                      >
                        DT Status:
                      </span>

                      <span
                        style={{
                          marginRight: '8px',
                          color: '#ACACAC',
                          fontSize: '12px'
                        }}
                      >
                        {dtStatusLabel}
                      </span>

                      <span
                        className={`${!isPlotSelected ? 'active' : 'non-active'
                          }`}
                      >
                        Plot
                      </span>
                      <Switch
                        onChange={(e) => togglePlotSwitch(e)}
                        checked={isPlotSelected}
                        size='small'
                      />
                      <span
                        className={`${isPlotSelected ? 'active' : 'non-active'
                          }`}
                      >
                        Table
                      </span>
                    </div>
                    <div>
                      {!_.isEmpty(summaryData.toggle) ? (
                        <div>
                          <span
                            className={`${!isUnitSelected ? 'active' : 'non-active'
                              }`}
                          >
                            {summaryData?.toggle[0].alias}
                          </span>
                          <Switch
                            onChange={(e) => toggleSwitch(e)}
                            checked={isUnitSelected}
                            size='small'
                          />
                          <span
                            className={`${isUnitSelected ? 'active' : 'non-active'
                              }`}
                          >
                            {summaryData?.toggle[1].alias}
                          </span>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                )}
              </div>

              <BarChartContainer
                sourceId={sourceId}
                moduleId={moduleId}
                scenarioId={scenarioId}
              />
            </Paper>
          </div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <ImpactAtPPGLevelTable />
      </Grid>
    </Grid>
  );
}